import React from "react";
import { useFormikContext } from "formik";
import { useSnackbar } from "components/haven-design/snackbar";
import { getApolloErrorMessage } from "utils/apollo";
import { SubmitFormResult } from "../form-definition-actions";
import { useFormBuilderStateContext } from "./form-builder-state-context";

type Props = {
  editorMode: "builder" | "toml";
  showSaveSuccess?: boolean;
  children: React.ReactNode;
};

/**
 * Replaces Formik &lt;Form&gt; so native submit (Save / Enter) runs applyBuilderToContent
 * before submitForm when the visual builder is active.
 */
const FormWithBuilderSubmit: React.FC<Props> = ({ editorMode, showSaveSuccess, children }) => {
  const { submitForm, setFieldValue } = useFormikContext();
  const { applyBuilderToContent } = useFormBuilderStateContext();
  const { openSnackbar } = useSnackbar();

  return (
    <form
      noValidate
      onSubmit={async (event) => {
        event.preventDefault();
        if (editorMode === "builder") {
          const built = await applyBuilderToContent();
          if (built === null) {
            openSnackbar({ message: "Fix builder errors before saving.", status: "error" });
            return;
          }
          await setFieldValue("content", built);
        }
        try {
          const saved = await submitForm() as SubmitFormResult | void;
          if (showSaveSuccess && saved?.saved) {
            openSnackbar({ message: "Update Successful", status: "success" });
          }
        } catch (err) {
          openSnackbar({ message: getApolloErrorMessage(err, "Update failed"), status: "error" });
        }
      }}
    >
      {children}
    </form>
  );
};

export default FormWithBuilderSubmit;
